import React from 'react';
import { motion } from 'framer-motion';
import LassoSelectIcon from '../icons/LassoSelectIcon';
import CheckIcon from '../icons/CheckIcon';
import Wand2Icon from '../icons/Wand2Icon';
import PenToolIcon from '../icons/PenToolIcon';
import GuideIcon from '../icons/GuideIcon';
import CropIcon from '../icons/CropIcon';

interface RefineToolbarProps {
    mouthPath: { x: number; y: number }[] | null;
    setIsMouthAnnotatorOpen: (isOpen: boolean) => void;
    isUpscaled: boolean;
    handleUpscaleImage: () => void;
    gumLinePath: { x: number; y: number }[] | null;
    setIsGumLineEditorOpen: (isOpen: boolean) => void;
    areGuidesDefined: boolean | null;
    setIsClinicalGuideEditorOpen: (isOpen: boolean) => void;
    setIsCroppingOneToOne: (isOpen: boolean) => void;
}

const RefineToolbar: React.FC<RefineToolbarProps> = ({
    mouthPath,
    setIsMouthAnnotatorOpen,
    isUpscaled,
    handleUpscaleImage,
    gumLinePath,
    setIsGumLineEditorOpen,
    areGuidesDefined,
    setIsClinicalGuideEditorOpen,
    setIsCroppingOneToOne,
}) => {
    return (
        <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            className="flex items-center gap-3 px-4 py-3 bg-white/90 dark:bg-zinc-900/90 backdrop-blur-xl rounded-full border border-white/20 dark:border-white/10 shadow-glass dark:shadow-glass-dark"
        >
            <ToolbarButton
                onClick={() => setIsCroppingOneToOne(true)}
                icon={<CropIcon className="w-4 h-4" />}
                label="Crop"
            />

            <div className="w-px h-6 bg-zinc-200 dark:bg-white/10 mx-1" />

            <ToolbarButton
                onClick={() => setIsMouthAnnotatorOpen(true)}
                icon={<LassoSelectIcon className="w-4 h-4" />}
                label="Mouth"
                isActive={!!mouthPath}
                activeColor="teal"
            />

            <ToolbarButton
                onClick={handleUpscaleImage}
                icon={<Wand2Icon className="w-4 h-4" />}
                label="Upscale"
                isActive={isUpscaled}
                activeColor="teal"
                disabled={isUpscaled}
            />

            <ToolbarButton
                onClick={() => setIsGumLineEditorOpen(true)}
                icon={<PenToolIcon className="w-4 h-4" />}
                label="Gums"
                isActive={!!gumLinePath}
                activeColor="teal"
            />

            <ToolbarButton
                onClick={() => setIsClinicalGuideEditorOpen(true)}
                icon={<GuideIcon className="w-4 h-4" />}
                label="Guides"
                isActive={!!areGuidesDefined}
                activeColor="teal"
            />
        </motion.div>
    );
};

interface ToolbarButtonProps {
    onClick: () => void;
    icon: React.ReactNode;
    label: string;
    isActive?: boolean;
    activeColor?: 'teal' | 'green';
    disabled?: boolean;
}

const ToolbarButton: React.FC<ToolbarButtonProps> = ({ onClick, icon, label, isActive, activeColor = 'teal', disabled }) => {
    return (
        <motion.button
            onClick={onClick}
            disabled={disabled}
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
            className={`
                relative flex flex-col items-center justify-center gap-1 px-3 py-1.5 rounded-xl transition-all duration-300
                ${isActive
                    ? 'text-teal-600 dark:text-teal-400 bg-teal-50 dark:bg-teal-900/20'
                    : 'text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-white/5'
                }
                ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
            `}
        >
            <div className={`p-1.5 rounded-full ${isActive ? 'bg-teal-100 dark:bg-teal-900/40' : 'bg-transparent'}`}>
                {icon}
            </div>
            <span className="text-[10px] font-bold uppercase tracking-wider">{label}</span>

            {isActive && (
                <motion.div
                    layoutId="activeDot"
                    className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-teal-500 rounded-full border-2 border-white dark:border-zinc-900 flex items-center justify-center"
                >
                    <CheckIcon className="w-1.5 h-1.5 text-white" />
                </motion.div>
            )}
        </motion.button>
    );
};

export default RefineToolbar;
